"use client";

import classNames from "classnames";
import {useTranslation} from 'react-i18next';

import FontAwesome from "../../utilities/FontAwesome";
import {LanguageDropDown} from './LanguageDropDown';
import styles from "../styles/LanguageTool.module.scss";
import {color_lightness} from "../../../utils/colorcontrast";
import {observer} from "mobx-react";
import {useStores} from "../../../stores/useStores";
import {useRef, useState} from "react";
import {Dialog} from "radix-ui";
import PoweredByGoogleTranslate from "../../../assets/images/greyscale-regular@2x.png";
import {ThemeProvider} from "../../radix/ThemeProvider";

export const LanguageSelector = (props: {
    handleSelect?: () => void,
    className?: string,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const translationClassName = classNames({
        [styles.translationElement]: true,
        [props.className || ""]: props.className,
    });

    const {t} = useTranslation('translation');

    let title;
    if (props.handleSelect) {
        // handleSelect is passed down for SchoolBlocks but not CleverFeed, where LanguageSelector is rendered within a Dialog and we need Dialog.Title for accessibility
        title = <Dialog.Title>{t('Select your language')}</Dialog.Title>
    } else {
        title = <h2>{t('Select your language')}</h2>
    }

    return <div className={translationClassName} ref={containerRef}>
        <div className={styles.inner}>
            <div className={styles.selectContainer}>
                <FontAwesome prefix={"fas"} name={"fa-language"} className={styles.icon} size={"2x"}/>
                {title}
            </div>
            <div className={styles.learnMoreContainer}>
                <div>{t('Set your preferred language. ') + ' '}</div>
                <img width={150} alt={"Powered by Google Translate"} src={PoweredByGoogleTranslate} />
            </div>
            <div className={styles.dropdownContainer}>
                <div className={styles.heading2}>
                    <span>{t('Language')}</span>
                </div>
                <ThemeProvider theme={{portalContainer: containerRef}}>
                    <LanguageDropDown handleSelect={props.handleSelect}/>
                </ThemeProvider>
            </div>
        </div>
    </div>
}

const LanguageTool = observer((props) => {
    const {styleStore} = useStores();
    const {t} = useTranslation('translation');
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const toggleOpen = () => setOpen(v => !v);

    let backgroundColor = "white", color = "black";
    if (styleStore.themeID === "1") {
        const lightness = color_lightness(styleStore.cssVars.primaryNavBackground);
        if (lightness > 50) {
            backgroundColor = "var(--secondaryNavBackground)";
            color = "var(--secondaryNavBackgroundTextColor)";
        }
    }

    const languageButtonContainerClassName = classNames({
        [styles.languageButtonContainer]: true,
        [styles.whiteText]: open,
    });

    const containerClassName = classNames({
        [styles.container]: true,
    });

    return (
        <Dialog.Root open={open} modal={false}>
            <div ref={ref} className={containerClassName} >
                <div className={styles.languageButtonShadow}/>
                <button data-testid={"language-trigger"} className={languageButtonContainerClassName} style={{backgroundColor, color}}
                        onClick={toggleOpen}>
                <span>
                    <div id={"arrows-small-width"}>
                        <FontAwesome prefix={"fas"} name={"fa-angle-down"} rotate={open ? 180 : undefined}/>
                    </div>
                    {t('Language')}
                    <div id={"arrows-large-width"}>
                        <FontAwesome prefix={"fas"} name={"fa-angle-down"} rotate={open ? 180 : undefined}/>
                    </div>
                </span>
                </button>
                <Dialog.Content asChild onPointerDownOutside={e => {
                    if (ref.current && !ref.current.contains(e.target as HTMLElement)) {
                        toggleOpen();
                    }
                }}>
                    <LanguageSelector handleSelect={toggleOpen} />
                </Dialog.Content>
            </div>
        </Dialog.Root>
    );
});

export default LanguageTool;
